import { createErrorFactory } from "@/shared/utils/error";

/**
 * Every event that can carry the visitor's permission to start audio. iOS
 * Safari only grants it at the end of a tap (`touchend`, `pointerup`,
 * `click`), never on `touchstart` or a touch's `pointerdown`; a mouse grants
 * it on `pointerdown`. Listening to all of them covers both.
 */
const kGestureEvents = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;

type ResumableContext = Pick<AudioContext, "state" | "resume">;

/**
 * Starts the page's AudioContext from the visitor's gestures, and restarts
 * it whenever the browser stops it.
 *
 * A one-shot unlock on the first event of a tap is refused on iOS (that
 * event is `pointerdown`) and never retried, so the page stays silent. iOS
 * also stops a running context for a phone call, the lock screen or another
 * app's audio. So the listeners stay for the life of the page and every
 * gesture retries until the context runs; once it does, each is a no-op.
 * Capture phase, so a component that stops propagation cannot swallow it.
 */
export const keepAudioContextRunning = (
    audioContext: ResumableContext,
    gestureTarget: EventTarget,
): void => {
    const resumeIfStopped = () => {
        if (audioContext.state === "running" || audioContext.state === "closed") {
            return;
        }
        audioContext.resume().catch(createErrorFactory("[keepAudioContextRunning]"));
    };
    for (const type of kGestureEvents) {
        gestureTarget.addEventListener(type, resumeIfStopped, { capture: true, passive: true });
    }
};

interface AudioSession {
    type: string;
}

const hasAudioSession = (
    value: Navigator,
): value is Navigator & { readonly audioSession: AudioSession } =>
    "audioSession" in value &&
    typeof value.audioSession === "object" &&
    value.audioSession !== null;

/**
 * Tells Safari this page is a music player, so an iPhone plays it with the
 * silent switch on. By default iOS files Web Audio alongside game and UI
 * sounds, which the switch mutes. Safari 16.4 and later only; other browsers
 * have no `audioSession` and skip this.
 */
export const requestPlaybackAudioSession = (navigatorLike: Navigator): void => {
    if (!hasAudioSession(navigatorLike)) {
        return;
    }
    navigatorLike.audioSession.type = "playback";
};
